/**
 * P&L calc — pricing the quality area's Daily Reports.
 *
 * Everything here is PURE and, per the rule in ./numbers.ts, never throws:
 * every computed value is a finite number or null. The inputs are the free
 * text people type on the Daily Report form — '78.48 MTons', '8.50 m3',
 * '8:00AM' — because the crew's form was deliberately left as it is (spec,
 * decision 1). What will not parse becomes an UNPRICED line with a reason,
 * never a zero; a total in this module never includes a guess.
 *
 * Spec: docs/superpowers/specs/2026-08-25-admin-pnl-design.md, §3.
 */

import { roundTo, toFinite } from './numbers';
import { quickTime } from './quick-entry';
import type {
  ActivityRate,
  ActivityUnit,
  DailyReportResponse,
  MaterialPrice,
  MixDesignPrice,
  PnlLabourLine,
  PnlMaterialLine,
  PnlOtherActivityLine,
  PnlPourLine,
  PnlProjectSummary,
  PnlSummary,
  PnlTruckingLine,
  TaxRate,
  TruckingRate,
  WorkerRate
} from '../types';

/* ==========================================================================
 * Parsing
 * ======================================================================== */

export type QuantityUnit = 't' | 'm3';

export interface Quantity {
  value: number;
  unit: QuantityUnit;
}

const TONNE_UNITS = new Set(['t', 'mt', 'mton', 'mtons', 'tonne', 'tonnes', 'ton', 'tons']);
const CUBE_UNITS = new Set(['m3', 'm³', 'cube', 'cubes', 'cum', 'cu.m', 'cu m', 'cu. m']);

/**
 * '78.48 MTons' -> { 78.48, 't' }; '8.50 m3' -> { 8.5, 'm3' }.
 *
 * A bare number takes `assume`: the paper omits units more often than it gets
 * them wrong. A recognised unit that is NOT the assumed one is still returned
 * as found, so the caller can say "this is in m³, not tonnes" rather than
 * silently pricing cubes as tonnes. Anything else — blank, 'Not recorded',
 * '150*', a negative, an unknown unit — is null.
 */
export function parseQuantity(text: string, assume: QuantityUnit): Quantity | null {
  const match = /^\s*(\d+(?:\.\d+)?)\s*([a-zA-Z0-9³.\s]*)$/.exec(text ?? '');
  if (!match) return null;
  const value = toFinite(match[1]);
  if (value === null || value < 0) return null;
  const rawUnit = match[2].trim().toLowerCase();
  if (rawUnit === '') return { value, unit: assume };
  if (TONNE_UNITS.has(rawUnit)) return { value, unit: 't' };
  if (CUBE_UNITS.has(rawUnit)) return { value, unit: 'm3' };
  return null;
}

/**
 * The concrete a list of pour rows adds up to: every `amount_supplied` that
 * reads as cubic metres, summed. This is the number under the m³ column of
 * every pours table — the admin sheet, the Daily and Contract Report forms and
 * their printed PDFs — so all four agree by construction.
 *
 * Rows that do not read as m³ contribute nothing rather than poisoning the
 * total: a blank row, 'Not recorded', or an amount typed in tonnes is left
 * out, exactly as `pricePours` leaves those rows unpriced. Returns null when
 * NO row reads, so the caller can show a dash instead of a misleading 0.
 */
export function totalCubes(amounts: readonly string[]): number | null {
  let sum: number | null = null;
  for (const amount of amounts) {
    const qty = parseQuantity(amount, 'm3');
    if (qty === null || qty.unit !== 'm3') continue;
    sum = (sum ?? 0) + qty.value;
  }
  return sum === null ? null : roundTo(sum, 2);
}

/**
 * Minutes since midnight, or null. Accepts '8:00AM', '9:25 am', '08:00',
 * '17:30', '8am', '12:00 PM'. A bare number with no am/pm is refused: '8'
 * could be either.
 */
export function parseClock(text: string): number | null {
  // Read what the crew actually types ('700', '5:pm', '330pm') the same way
  // the form does on blur, so a sheet saved before quick entry existed prices
  // like one saved after it.
  const match = /^\s*(\d{1,2})(?::(\d{2}))?\s*([aApP][mM])?\s*$/.exec(quickTime(text ?? ''));
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = match[2] === undefined ? 0 : Number(match[2]);
  const meridiem = match[3]?.toLowerCase();
  if (match[2] === undefined && meridiem === undefined) return null;
  if (minute > 59) return null;
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    if (meridiem === 'am' && hour === 12) hour = 0;
    if (meridiem === 'pm' && hour !== 12) hour += 12;
  } else if (hour > 23) {
    return null;
  }
  return hour * 60 + minute;
}

/** Hours from `inText` to `outText`, 2 dp; null if unreadable or not positive. */
export function hoursBetween(inText: string, outText: string): number | null {
  const start = parseClock(inText);
  const end = parseClock(outText);
  if (start === null || end === null) return null;
  if (end <= start) return null;
  return roundTo((end - start) / 60, 2);
}

/** Trim, collapse internal whitespace, lower-case: the matching key for names. */
export function foldName(text: string): string {
  return (text ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * The materials freight can be charged on, as the Daily Report spells them.
 * Admixture is deliberately absent: it arrives with the load, never on a
 * truck the owner pays for by the tonne.
 */
export const TRUCKED_MATERIALS: readonly string[] = ['Sand', 'Gravel', 'Cement'] as const;

/**
 * The folded names a trucking rate is charged on, or null for "every material
 * on the report".
 *
 * Null is LEGACY: the empty string is what a rate row entered before this
 * column existed means, and those rows must keep pricing as they did. Every
 * rate written since names its materials outright, so nothing new lands here
 * — do not treat null as the default for a new rate.
 */
export function chargedMaterials(rate: { materials?: string } | null | undefined): Set<string> | null {
  const named = (rate?.materials ?? '')
    .split(',')
    .map(foldName)
    .filter((name) => name !== '');
  return named.length === 0 ? null : new Set(named);
}

/**
 * How many of an Other activity: '2', '1.5', '2 loads', or a clock-style
 * duration '1:30' (= 1.5, for Standby hours). Null when nothing readable, or
 * when the figure is not above zero — a zero row is not a charge, and a
 * negative one is a typo, never a credit.
 */
export function parseCount(text: string): number | null {
  const t = text.trim();
  const clock = /^(\d+):([0-5]\d)\b/.exec(t);
  if (clock) {
    const value = Number(clock[1]) + Number(clock[2]) / 60;
    return value > 0 ? roundTo(value, 4) ?? null : null;
  }
  const m = /^(\d+(?:\.\d+)?|\.\d+)/.exec(t.replace(/,/g, ''));
  if (!m) return null;
  const value = Number(m[1]);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * The Other activities a Daily Report offers before any rate exists — what
 * the owner asked for by name. Each carries the unit its rate is quoted in.
 */
export const DEFAULT_OTHER_ACTIVITIES: readonly { name: string; unit: ActivityUnit }[] = [
  { name: 'Standby', unit: 'hour' },
  { name: 'Delivery', unit: 'load' },
  { name: 'Small load fee', unit: 'each' }
] as const;

/* ==========================================================================
 * Price in force
 * ======================================================================== */

/**
 * The row with the greatest `effective_from` on or before `onDate`, or null.
 * Dates are ISO 'YYYY-MM-DD', so string comparison IS date comparison.
 */
export function priceInForce<T extends { id: number; effective_from: string }>(
  rows: readonly T[],
  onDate: string
): T | null {
  let best: T | null = null;
  for (const row of rows) {
    if (row.effective_from > onDate) continue;
    if (best === null || row.effective_from > best.effective_from) {
      best = row;
      // Same day, entered later: a price is never edited, so correcting a
      // row means adding another one on the same effective date. The newer
      // row is the correction and has to win, or the mistake stands.
    } else if (row.effective_from === best.effective_from && row.id > best.id) {
      best = row;
    }
  }
  return best;
}

/** Every price row in the database, as the routes load them. */
export interface AllPrices {
  materials: readonly MaterialPrice[];
  workers: readonly WorkerRate[];
  mixes: readonly MixDesignPrice[];
  trucking: readonly TruckingRate[];
  taxes: readonly TaxRate[];
  /** Optional so a caller that predates Other activities still prices. */
  activities?: readonly ActivityRate[];
}

/** The rates that apply on one date, keyed for lookup. */
export interface PricesInForce {
  /** The DEFAULT price per folded material: rows with no site. */
  materials: Map<string, MaterialPrice>;
  /** A site's own prices, keyed site id then folded material; checked before `materials`. */
  materialsBySite: Map<number, Map<string, MaterialPrice>>;
  /** Default activity rates by folded name, then each site's own — same fallback as materials. */
  activities: Map<string, ActivityRate>;
  activitiesBySite: Map<number, Map<string, ActivityRate>>;
  workers: Map<string, WorkerRate>;
  mixes: Map<number, MixDesignPrice>;
  /** Keyed by site id. A site absent here falls back to `truckingDefault`. */
  trucking: Map<number, TruckingRate>;
  truckingDefault: TruckingRate | null;
  /** Percentages, so 5 means 5%. Nothing in force means nothing is charged. */
  gstPercent: number;
  pstPercent: number;
  onDate: string;
  /**
   * Does the job this report belongs to bill for the concrete it pours?
   * Defaults true, so every caller that has not been taught about
   * `job_site.bills_concrete` prices exactly as it always has.
   */
  billsConcrete: boolean;
}

function groupBy<T>(rows: readonly T[], key: (row: T) => string | number): Map<string | number, T[]> {
  const groups = new Map<string | number, T[]>();
  for (const row of rows) {
    const k = key(row);
    const group = groups.get(k);
    if (group) group.push(row);
    else groups.set(k, [row]);
  }
  return groups;
}

/**
 * Splits site-scoped rows into the default map (site_id null) and one map per
 * site, each holding the row in force per folded name. A site's own row wins
 * over the default only for that site; the default still prices every other.
 */
function bySiteInForce<T extends { id: number; effective_from: string; site_id?: number | null }>(
  rows: readonly T[],
  name: (row: T) => string,
  onDate: string
): { fallback: Map<string, T>; bySite: Map<number, Map<string, T>> } {
  const fallback = new Map<string, T>();
  const bySite = new Map<number, Map<string, T>>();
  for (const [key, group] of groupBy(rows, (r) => `${r.site_id ?? 'default'}|${foldName(name(r))}`)) {
    const row = priceInForce(group, onDate);
    if (!row) continue;
    const k = key as string;
    const fold = k.slice(k.indexOf('|') + 1);
    const site = row.site_id ?? null;
    if (site === null) fallback.set(fold, row);
    else {
      const map = bySite.get(site) ?? new Map<string, T>();
      map.set(fold, row);
      bySite.set(site, map);
    }
  }
  return { fallback, bySite };
}

export function pricesInForce(all: AllPrices, onDate: string, billsConcrete = true): PricesInForce {
  const mat = bySiteInForce(all.materials, (r) => r.material, onDate);
  const materials = mat.fallback;
  const act = bySiteInForce(all.activities ?? [], (r) => r.activity, onDate);
  const workers = new Map<string, WorkerRate>();
  for (const [key, rows] of groupBy(all.workers, (r) => foldName(r.name))) {
    const row = priceInForce(rows, onDate);
    if (row) workers.set(key as string, row);
  }
  const mixes = new Map<number, MixDesignPrice>();
  for (const [key, rows] of groupBy(all.mixes, (r) => r.mix_design_id)) {
    const row = priceInForce(rows, onDate);
    if (row) mixes.set(key as number, row);
  }
  const trucking = new Map<number, TruckingRate>();
  let truckingDefault: TruckingRate | null = null;
  for (const [key, rows] of groupBy(all.trucking, (r) => r.site_id ?? 'default')) {
    const row = priceInForce(rows, onDate);
    if (!row) continue;
    if (key === 'default') truckingDefault = row;
    else trucking.set(key as number, row);
  }
  const taxes = groupBy(all.taxes, (r) => r.kind);
  const percentOf = (kind: string) => priceInForce(taxes.get(kind) ?? [], onDate)?.percent ?? 0;
  return {
    materials,
    materialsBySite: mat.bySite,
    activities: act.fallback,
    activitiesBySite: act.bySite,
    workers,
    mixes,
    trucking,
    truckingDefault,
    gstPercent: percentOf('gst'),
    pstPercent: percentOf('pst'),
    onDate,
    billsConcrete
  };
}

/* ==========================================================================
 * Pricing one report
 * ======================================================================== */

/** The fixed vocabulary of unpriced reasons — these are the words on screen. */
export const UNPRICED = {
  noClockIn: 'no clock-in',
  noClockOut: 'no clock-out',
  outBeforeIn: 'clock-out before clock-in',
  noRate: (name: string) => `no rate for "${name}"`,
  notReadable: (text: string) => `amount not readable: "${text}"`,
  cubesNotTonnes: 'amount is in m³, not tonnes',
  tonnesNotCubes: 'amount is in tonnes, not m³',
  noMaterialPrice: (material: string, onDate: string) => `no price for "${material}" on ${onDate}`,
  noMixChosen: 'no mix design chosen',
  noMixPrice: (code: string, onDate: string) => `no price for ${code} on ${onDate}`,
  noTruckingRate: (onDate: string) => `no trucking rate on ${onDate}`,
  noActivityRate: (activity: string, onDate: string) => `no rate for "${activity}" on ${onDate}`,
  noActivityChosen: 'no activity chosen',
  quantityNotReadable: (text: string) => `how many not readable: "${text}"`
} as const;

export interface ReportPnl {
  report_id: number;
  report_date: string;
  project: string;
  /** The job site this report is filed under, or null when it is loose text. */
  site_id: number | null;
  labour: PnlLabourLine[];
  materials: PnlMaterialLine[];
  /** At most one line: freight is charged once, over the whole day's tonnes. */
  trucking: PnlTruckingLine[];
  pours: PnlPourLine[];
  /** Standby, Delivery, Small load fee, ... — revenue at the activity rates. */
  other_activities: PnlOtherActivityLine[];
  summary: PnlSummary;
}

export function emptySummary(): PnlSummary {
  return {
    revenue: 0,
    cost: 0,
    margin: 0,
    margin_pct: null,
    labour: 0,
    materials: 0,
    hired_in: 0,
    trucking: 0,
    pst_cost: 0,
    gst_collected: 0,
    gst_paid: 0,
    contract_revenue: 0,
    unpriced_count: 0
  };
}

/**
 * Margin as a percent of revenue. Null when there is no revenue: a day that
 * cost money and poured nothing has no percentage, and 0% would read as
 * breaking even.
 */
function marginPct(revenue: number, margin: number): number | null {
  if (revenue === 0) return null;
  return roundTo((margin / revenue) * 100, 1) ?? null;
}

function money(value: number): number {
  return roundTo(value, 2) ?? 0;
}

/**
 * Cost one labour line: a name with a claimed clock-in and clock-out,
 * priced against `worker_rate.hourly_rate` — never `operator_rate.charge_per_day`,
 * a different number that happens to hang off the same name and feeds
 * REVENUE, not cost (calc/contract.ts's header). This is the one rule labour
 * is costed by: `priceReport` calls it for a Daily Report's personnel, and
 * the admin P&L route calls it for a contract day's operators (same three
 * fields — name/time_in/time_out — plus `days`, which bills and plays no
 * part here), so a change to how labour is costed can never apply to one
 * kind of day and not the other.
 */
export function priceLabourLine(
  person: { name: string; time_in: string; time_out: string },
  stamp: { report_id: number; report_date: string; ticket_no: string },
  workers: ReadonlyMap<string, WorkerRate>
): PnlLabourLine {
  const base = { ...stamp, name: person.name, time_in: person.time_in, time_out: person.time_out };
  const start = parseClock(person.time_in);
  const end = parseClock(person.time_out);
  if (start === null) return { ...base, hours: null, rate: null, cost: null, reason: UNPRICED.noClockIn };
  if (end === null) return { ...base, hours: null, rate: null, cost: null, reason: UNPRICED.noClockOut };
  const hours = hoursBetween(person.time_in, person.time_out);
  if (hours === null) return { ...base, hours: null, rate: null, cost: null, reason: UNPRICED.outBeforeIn };
  const rate = workers.get(foldName(person.name));
  if (!rate) return { ...base, hours, rate: null, cost: null, reason: UNPRICED.noRate(person.name) };
  return { ...base, hours, rate: rate.hourly_rate, cost: money(hours * rate.hourly_rate), reason: null };
}

/** The fields `pricePours` needs off a pour row — `DailyReportActivity` and `ContractReportActivity` both satisfy this. */
export interface PourInput {
  piling_number: string;
  unit_number: string;
  amount_supplied: string;
  water_used: string;
  mix_design_id: number | null;
}

/**
 * Prices a set of pour rows the way a Daily Report's `activities` always
 * have: m³ parsed from the free text, priced against the mix chosen on the
 * row, and `bills_concrete` honoured exactly as it is here. Pulled out of
 * `priceReport` so a Contract Report's own pours (`contract_report_activity`
 * — same shape as `daily_report_activity`, spec's own words) are priced by
 * this ONE rule rather than a second copy of it living in the admin P&L
 * route. `priceReport` below is now just this function's first caller.
 */
export function pricePours(
  activities: readonly PourInput[],
  stamp: { report_id: number; report_date: string; ticket_no: string },
  prices: PricesInForce
): PnlPourLine[] {
  return activities.map((activity) => {
    const base = {
      ...stamp,
      piling_number: activity.piling_number,
      unit_number: activity.unit_number,
      amount_text: activity.amount_supplied,
      mix_code: activity.water_used
    };
    const qty = parseQuantity(activity.amount_supplied, 'm3');
    if (qty === null) {
      return { ...base, m3: null, rate: null, revenue: null, reason: UNPRICED.notReadable(activity.amount_supplied) };
    }
    if (qty.unit !== 'm3') {
      return { ...base, m3: null, rate: null, revenue: null, reason: UNPRICED.tonnesNotCubes };
    }
    // A job whose client supplies the material bills no concrete. The pour is
    // still recorded — it is a quality record — but it earns nothing and is
    // NOT unpriced: it is deliberately unbilled, and flagging it would nag
    // forever about something that is correct.
    if (!prices.billsConcrete) {
      return { ...base, m3: qty.value, rate: null, revenue: null, reason: null };
    }
    if (activity.mix_design_id === null) {
      return { ...base, m3: qty.value, rate: null, revenue: null, reason: UNPRICED.noMixChosen };
    }
    const price = prices.mixes.get(activity.mix_design_id);
    if (!price) {
      return {
        ...base, m3: qty.value, rate: null, revenue: null,
        reason: UNPRICED.noMixPrice(activity.water_used || `mix #${activity.mix_design_id}`, prices.onDate)
      };
    }
    return { ...base, m3: qty.value, rate: price.sell_per_m3, revenue: money(qty.value * price.sell_per_m3), reason: null };
  });
}

/**
 * Prices a report's Other activities: how many times the rate in force for
 * that activity at the report's site, falling back to the default rate. A
 * blank row (no activity and no quantity) is the form's spare row: skipped.
 */
export function priceOtherActivities(
  rows: readonly { activity: string; quantity: string }[],
  stamp: { report_id: number; report_date: string; ticket_no: string },
  site_id: number | null,
  prices: PricesInForce
): PnlOtherActivityLine[] {
  return rows
    .filter((row) => row.activity.trim() !== '' || row.quantity.trim() !== '')
    .map((row) => {
      const base = { ...stamp, activity: row.activity, quantity_text: row.quantity };
      const unpriced = (reason: string, quantity: number | null = null): PnlOtherActivityLine => ({
        ...base, quantity, unit: null, rate: null, revenue: null, site_id: null, reason
      });
      if (row.activity.trim() === '') return unpriced(UNPRICED.noActivityChosen);
      const quantity = parseCount(row.quantity);
      if (quantity === null) return unpriced(UNPRICED.quantityNotReadable(row.quantity));
      const fold = foldName(row.activity);
      const own = site_id === null ? undefined : prices.activitiesBySite.get(site_id)?.get(fold);
      const rate = own ?? prices.activities.get(fold);
      if (!rate) return unpriced(UNPRICED.noActivityRate(row.activity, prices.onDate), quantity);
      return {
        ...base,
        quantity,
        unit: rate.unit,
        rate: rate.rate,
        revenue: money(quantity * rate.rate),
        site_id: own ? site_id : null,
        reason: null
      };
    });
}

export function priceReport(report: DailyReportResponse, prices: PricesInForce): ReportPnl {
  const { id: report_id, report_date, ticket_no, project } = report.daily_report;
  // Loose default: a report loaded from a shape that omits site_id (an old
  // row, an untyped join) must group as text, never as the literal group
  // "site:undefined" merging with every other reportless report.
  const site_id = report.daily_report.site_id ?? null;
  const stamp = { report_id, report_date, ticket_no };

  const labour: PnlLabourLine[] = report.personnel.map((person) => priceLabourLine(person, stamp, prices.workers));

  const materials: PnlMaterialLine[] = report.deliveries
    // A blank amount is nothing delivered — the paper's spare rows — not a
    // problem to flag.
    .filter((delivery) => delivery.amount.trim() !== '')
    .map((delivery) => {
      const base = { ...stamp, material: delivery.material, amount_text: delivery.amount };
      const qty = parseQuantity(delivery.amount, 't');
      if (qty === null) {
        return { ...base, tonnes: null, rate: null, cost: null, reason: UNPRICED.notReadable(delivery.amount) };
      }
      if (qty.unit !== 't') {
        return { ...base, tonnes: null, rate: null, cost: null, reason: UNPRICED.cubesNotTonnes };
      }
      // The site's own price first, then the default — a site without its
      // own price for this material is priced exactly as before.
      const fold = foldName(delivery.material);
      const price =
        (site_id === null ? undefined : prices.materialsBySite.get(site_id)?.get(fold)) ?? prices.materials.get(fold);
      if (!price) {
        return {
          ...base, tonnes: qty.value, rate: null, cost: null,
          reason: UNPRICED.noMaterialPrice(delivery.material, prices.onDate)
        };
      }
      return { ...base, tonnes: qty.value, rate: price.cost_per_tonne, cost: money(qty.value * price.cost_per_tonne), reason: null };
    });

  const pours: PnlPourLine[] = pricePours(report.activities, stamp, prices);

  // Freight is bought by the tonne on what actually arrived, so it is priced
  // off the delivery tonnes rather than the delivery costs: a load with no
  // material price was still trucked. A day with nothing readable to deliver
  // gets no trucking line at all — flagging a rate that nothing needed would
  // bury the flags that do mean something, and a rate charged on nothing that
  // turned up today needed nothing either.
  const truckingRate = site_id === null ? null : prices.trucking.get(site_id) ?? null;
  const rate = truckingRate ?? prices.truckingDefault;
  // With no rate resolved there is no list to filter by, so the flagged line
  // still carries the whole day's tonnes — that is the figure the owner needs
  // to see when he goes and sets the rate.
  const chargedOn = chargedMaterials(rate);
  const tonnesDelivered = materials.reduce(
    (sum, m) => (chargedOn && !chargedOn.has(foldName(m.material)) ? sum : sum + (m.tonnes ?? 0)),
    0
  );
  const trucking: PnlTruckingLine[] =
    tonnesDelivered === 0
      ? []
      : [
          {
            ...stamp,
            site_id: truckingRate === null ? null : site_id,
            tonnes: roundTo(tonnesDelivered, 2),
            rate: rate?.cost_per_tonne ?? null,
            cost: rate ? money(tonnesDelivered * rate.cost_per_tonne) : null,
            reason: rate ? null : UNPRICED.noTruckingRate(prices.onDate)
          }
        ];

  const other_activities = priceOtherActivities(report.other_activities ?? [], stamp, site_id, prices);

  // Other activities are BILLED, like concrete, so they join revenue and
  // carry GST collected; they are not a cost of anything.
  const revenue = money(
    pours.reduce((sum, p) => sum + (p.revenue ?? 0), 0) +
      other_activities.reduce((sum, o) => sum + (o.revenue ?? 0), 0)
  );
  const labourCost = labour.reduce((sum, l) => sum + (l.cost ?? 0), 0);
  const materialCost = materials.reduce((sum, m) => sum + (m.cost ?? 0), 0);
  const truckingCost = trucking.reduce((sum, t) => sum + (t.cost ?? 0), 0);
  // Every price in this app is ex-tax. PST is not recoverable, so it is spent
  // and belongs in cost; GST is recoverable, so it is neither cost nor
  // revenue and is only tracked so the owner can see what is owing.
  const pst_cost = money(materialCost * prices.pstPercent / 100);
  const gst_collected = money(revenue * prices.gstPercent / 100);
  const gst_paid = money((materialCost + truckingCost) * prices.gstPercent / 100);
  const cost = money(labourCost + materialCost + pst_cost + truckingCost);
  const unpriced_count =
    labour.filter((l) => l.reason !== null).length +
    materials.filter((m) => m.reason !== null).length +
    trucking.filter((t) => t.reason !== null).length +
    pours.filter((p) => p.reason !== null).length +
    other_activities.filter((o) => o.reason !== null).length;

  return {
    report_id,
    report_date,
    project,
    site_id,
    labour,
    materials,
    trucking,
    pours,
    other_activities,
    summary: {
      revenue,
      cost,
      margin: money(revenue - cost),
      margin_pct: marginPct(revenue, money(revenue - cost)),
      labour: money(labourCost),
      materials: money(materialCost),
      // A daily report has no hires — only a contract card can hire anything
      // in, and `addContractRevenue` is the only thing that fills this.
      hired_in: 0,
      trucking: money(truckingCost),
      pst_cost,
      gst_collected,
      gst_paid,
      // A Daily Report earns no contract revenue itself; the route folds a
      // job's contract reports in alongside it (calc/contract.ts).
      contract_revenue: 0,
      unpriced_count
    }
  };
}

/* ==========================================================================
 * Aggregation
 * ======================================================================== */

export function sumSummaries(parts: readonly PnlSummary[]): PnlSummary {
  const total = (of: (p: PnlSummary) => number) => money(parts.reduce((s, p) => s + of(p), 0));
  const revenue = total((p) => p.revenue);
  const cost = total((p) => p.cost);
  return {
    revenue,
    cost,
    margin: money(revenue - cost),
    margin_pct: marginPct(revenue, money(revenue - cost)),
    labour: total((p) => p.labour),
    materials: total((p) => p.materials),
    hired_in: total((p) => p.hired_in),
    trucking: total((p) => p.trucking),
    pst_cost: total((p) => p.pst_cost),
    gst_collected: total((p) => p.gst_collected),
    gst_paid: total((p) => p.gst_paid),
    contract_revenue: total((p) => p.contract_revenue),
    unpriced_count: parts.reduce((s, p) => s + p.unpriced_count, 0)
  };
}

/**
 * Folds contract revenue AND its operators' labour cost onto an
 * already-priced summary: money only, margin and its percentage recomputed
 * from the new revenue and cost. Nothing else on the summary changes — a
 * contract day is not a pour, and does not become one just because its
 * money lands in the same total (spec: contract and pour revenue ADD, never
 * replace). A zero addition on every count returns the summary unchanged,
 * so folding "nothing" into every project every month allocates nothing.
 *
 * `contractCost` is what a contract day's operators COST — hours × their
 * `worker_rate.hourly_rate`, priced by the same `priceLabourLine` a Daily
 * Report's personnel is (Task 5b). It is wages, so — same as a Daily
 * Report's — it is folded into `labour` as well as `cost`; the invariant
 * `cost === labour + materials + pst_cost + trucking` (PnlSummary's own
 * doc-comment) has to keep holding after a contract day joins the total, not
 * just before one does.
 *
 * `contractUnpriced` is a contract line with no rate set — an equipment or
 * operator name nobody has priced (revenue side), or an operator with no
 * clock-in/out or no worker_rate (cost side, Task 5b). Either earns $0 and
 * must show up as unpriced the same way a Daily Report line does, or a
 * missing rate is invisible everywhere the owner would look for it
 * (contract.ts's own header warns about exactly this, for its own half).
 *
 * `contractGstCollected` is GST on contract revenue, computed by the caller
 * per report at the rate in force on that report's own date — this module
 * has no report dates to resolve that with once revenue has been summed.
 * It is recoverable, so — like the GST a pour collects — it sits in
 * `gst_collected` only, never in `revenue` or `cost`. Labour is never taxed
 * by GST in this app (a Daily Report's own wages are not either — see
 * `priceReport`'s `gst_paid`), so `contractCost` never touches `gst_paid`.
 *
 * Callers that also carry `margin_per_day` (a `PnlProjectSummary` or an
 * `AdminSiteRow`) must recompute it themselves after calling this — see
 * `withMarginPerDay` — because this function does not know `days_worked`.
 */
export function addContractRevenue<T extends PnlSummary>(
  summary: T,
  contractRevenue: number,
  contractCost = 0,
  contractUnpriced = 0,
  contractGstCollected = 0,
  /**
   * How much of `contractCost` was paid to third parties hired IN, as opposed
   * to SCI's own crew. `contractCost` stays the WHOLE cost — this only says
   * how to split it between the `labour` and `hired_in` lines, so a caller
   * that does not pass it keeps the old behaviour of counting all of it as
   * wages, which for a caller with no hires is still correct.
   */
  contractHiresCost = 0
): T {
  if (contractRevenue === 0 && contractCost === 0 && contractUnpriced === 0 && contractGstCollected === 0) {
    return summary;
  }
  const revenue = money(summary.revenue + contractRevenue);
  const cost = money(summary.cost + contractCost);
  const margin = money(revenue - cost);
  return {
    ...summary,
    revenue,
    cost,
    // A rented machine is not a wage (re-review #4, Important E). The two
    // lines below split `contractCost`; they never add to it.
    labour: money(summary.labour + contractCost - contractHiresCost),
    hired_in: money(summary.hired_in + contractHiresCost),
    contract_revenue: money(summary.contract_revenue + contractRevenue),
    margin,
    margin_pct: marginPct(revenue, margin),
    gst_collected: money(summary.gst_collected + contractGstCollected),
    unpriced_count: summary.unpriced_count + contractUnpriced
  };
}

/**
 * Recomputes `margin_per_day` from the row's current `margin` and
 * `days_worked` — null when there are no days, same rule `groupReports`
 * itself uses. Call this after `addContractRevenue` on anything that
 * carries `margin_per_day`: that function changes `margin` but does not
 * know about days worked, so the derived field would otherwise go stale.
 */
export function withMarginPerDay<T extends { margin: number; days_worked: number; margin_per_day: number | null }>(
  row: T
): T {
  return { ...row, margin_per_day: row.days_worked === 0 ? null : (roundTo(row.margin / row.days_worked, 2) ?? null) };
}

/**
 * Reports grouped into projects: by site id when the report is linked, else
 * by folded project text. Sorted by revenue desc, then name. The dates and
 * day count are derived from the reports themselves, so per-day margin needs
 * no bookkeeping.
 *
 * A linked group's `project` is the first spelling seen on a report; the
 * route substitutes the site's own name, because reading `job_site` is the
 * database's job and this module never touches it.
 */
export function groupReports(reports: readonly ReportPnl[]): PnlProjectSummary[] {
  const groups = new Map<string, { project: string; site_id: number | null; parts: ReportPnl[] }>();
  for (const report of reports) {
    const key = report.site_id == null ? `text:${foldName(report.project)}` : `site:${report.site_id}`;
    const group = groups.get(key);
    if (group) group.parts.push(report);
    else groups.set(key, { project: report.project.trim(), site_id: report.site_id, parts: [report] });
  }
  return [...groups.values()]
    .map((group) => {
      const dates = [...new Set(group.parts.map((p) => p.report_date).filter((d) => d !== ''))].sort();
      const summary = sumSummaries(group.parts.map((p) => p.summary));
      const days = dates.length;
      return {
        ...summary,
        project: group.project,
        site_id: group.site_id,
        report_count: group.parts.length,
        first_report_date: dates[0] ?? null,
        last_report_date: dates[dates.length - 1] ?? null,
        days_worked: days,
        margin_per_day: days === 0 ? null : (roundTo(summary.margin / days, 2) ?? null)
      };
    })
    .sort((a, b) => b.revenue - a.revenue || a.project.localeCompare(b.project));
}
